/**
 * useMCPCorrections — list `mcp_corrections` rows for the active org.
 *
 * RLS scopes by org (super_admin sees every org); the contractor_admin /
 * super_admin role gate is enforced client-side at the route level so
 * non-admins never reach the page that calls this hook. See
 * migration 20260501000009 §mcp_corrections_select for the policy.
 *
 * Defaults to active rows only — the admin UI shows what is currently
 * filtering output. The list is short (typically dozens, not thousands)
 * because corrections are user-flagged events, so we fetch up to 200 with
 * a single query and filter / sort client-side.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'
import type { MCPCorrectionRow, McpCorrectionCategory } from '@/types/mcpCorrection'

export interface UseMCPCorrectionsArgs {
  /** When set, only rows whose category matches are returned. */
  category?: McpCorrectionCategory
}

export function useMCPCorrections(args: UseMCPCorrectionsArgs = {}): UseQueryResult<MCPCorrectionRow[]> {
  const supabase = useSupabase()
  return useQuery({
    queryKey: ['mcp-corrections', args.category ?? 'all'],
    queryFn: async (): Promise<MCPCorrectionRow[]> => {
      let q = supabase
        .from('mcp_corrections')
        .select('id, organization_id, server_name, tool_name, category, reason, reported_by, created_at, is_active')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(200)

      if (args.category) q = q.eq('category', args.category)

      const { data, error } = await q
      if (error) throw error
      return (data ?? []) as unknown as MCPCorrectionRow[]
    },
    refetchInterval: 60_000,
  })
}
